import React, { Fragment, memo, useCallback, useState } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { Button } from 'src/components/shared/Button'
import { SectionCard } from 'src/components/shared/SectionCard'
import { AdminDeletePartnerDriverData, ClearPartnerDriverState } from 'src/constants/actionTypes'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import type { PartnerDriverDeletionReason } from 'src/constants/partnerDriver/enums'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useHasPermission } from 'src/hooks/useHasPermission'
import type { PartnerDriverDetails } from 'src/interfaces/partnerDriver'
import { getPartnerDriverSaving } from 'src/selectors/partnerDriver'
import { ContractorDeletionSummary } from './ContractorDeletionSummary'
import { DeleteContractorModal } from './DeleteContractorModal'

interface Props {
  driver: PartnerDriverDetails
  /** The contractor search, with its query — where the page goes once the driver is deleted. */
  backTo: { pathname: string; search: string }
}

/** ENG-1214. Hidden without `contractor_data_delete`, the permission the route requires. */
const DeleteContractorCardComponent = ({ driver, backTo }: Props) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const snackbar = useSnackbar()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()
  const isSaving = useSelector(getPartnerDriverSaving)
  const canDeleteContractor = useHasPermission(InternalPermission.ContractorDataDelete)
  const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false)

  const handleOpenConfirm = useCallback(() => {
    setIsConfirmOpen(true)
  }, [])

  // Escape and the backdrop must not dismiss the modal while the delete is in flight.
  const handleCloseConfirm = useCallback(() => {
    if (isSaving) {
      return
    }

    setIsConfirmOpen(false)
  }, [isSaving])

  const handleConfirm = useCallback(
    async (reason: PartnerDriverDeletionReason) => {
      const result = await asyncDispatchWithErrorToast<boolean>(
        AdminDeletePartnerDriverData.request({ partnerDriverId: driver.id, reason }),
      )

      // Failure already showed its toast; the modal stays open with the chosen reason.
      if (!result) {
        return
      }

      snackbar?.enqueueSnackbar('Contractor deleted', { variant: 'success' })
      setIsConfirmOpen(false)
      navigate(backTo)
      dispatch(ClearPartnerDriverState())
    },
    [asyncDispatchWithErrorToast, driver.id, snackbar, navigate, backTo, dispatch],
  )

  if (!canDeleteContractor) {
    return null
  }

  return (
    <Fragment>
      <SectionCard
        title="Delete contractor"
        dataTestId="partner-driver-delete-contractor-card"
        actions={
          <Button
            size="small"
            color="red"
            disabled={isSaving}
            onClick={handleOpenConfirm}
            data-testid="partner-driver-delete-contractor-button"
          >
            Delete contractor
          </Button>
        }
      >
        <Typography variant="body2" css={tw`text-shades-500`}>
          Permanently deletes this contractor's data.
        </Typography>
        <ContractorDeletionSummary driver={driver} />
      </SectionCard>
      {isConfirmOpen ? (
        <DeleteContractorModal
          isOpen
          driver={driver}
          isDeleting={isSaving}
          onConfirm={handleConfirm}
          onClose={handleCloseConfirm}
        />
      ) : null}
    </Fragment>
  )
}

export const DeleteContractorCard = memo(DeleteContractorCardComponent)
